import React from 'react';
import { Badge } from '@/components/ui/badge';
import { DomainPricing } from '@/types';
import {
  formatPrice,
  getPricingBadgeColor,
  isPremiumDomain,
  isPurchasableDomain,
  getPricingSummary,
  getPrimaryPrice
} from '@/lib/pricing-utils';
import { DollarSign, Crown, AlertTriangle, Info, TrendingDown } from 'lucide-react';
import nameComLogo from '@/assets/logo_name_com.png';
import namecheapLogo from '@/assets/namecheap.svg';

interface DomainPricingProps {
  pricing?: DomainPricing | null;
  pricingSources?: DomainPricing[];
  showDetails?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const DomainPricingComponent: React.FC<DomainPricingProps> = ({
  pricing,
  pricingSources,
  showDetails = false,
  size = 'md',
  className = ''
}) => {
  // Use pricingSources if available, otherwise fallback to pricing
  const sources = pricingSources && pricingSources.length > 0 ? pricingSources : (pricing ? [pricing] : []);
  const primaryPricing = pricing || (sources.length > 0 ? sources[0] : null);

  if (!primaryPricing && sources.length === 0) {
    return (
      <div className={`flex items-center gap-1 ${className}`}>
        <Badge variant="outline" className="bg-gray-100 text-gray-700 font-bold shadow-sm">
          <Info className="w-3 h-3 mr-1" />
          Pricing unavailable
        </Badge>
      </div>
    );
  }

  // If we have multiple sources and showDetails is true, show comparison table
  if (showDetails && sources.length > 1) {
    // Use backend-calculated values if available, otherwise calculate on frontend (fallback)
    const getBestPrice = (priceType: 'purchasePrice' | 'renewalPrice' | 'transferPrice') => {
      const prices = sources
        .map(s => s[priceType])
        .filter((p): p is number => p !== null && p !== undefined);
      return prices.length > 0 ? Math.min(...prices) : null;
    };

    const bestPurchase = getBestPrice('purchasePrice');
    const bestRenewal = getBestPrice('renewalPrice');
    const bestTransfer = getBestPrice('transferPrice');

    return (
      <div className={`space-y-4 ${className}`}>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-600">
                <th className="text-left py-3 px-4 font-semibold text-slate-900 dark:text-white">Provider</th>
                <th className="text-right py-3 px-4 font-semibold text-slate-900 dark:text-white">Purchase</th>
                <th className="text-right py-3 px-4 font-semibold text-slate-900 dark:text-white">Renewal</th>
                <th className="text-right py-3 px-4 font-semibold text-slate-900 dark:text-white">Transfer</th>
              </tr>
            </thead>
            <tbody>
              {sources.map((source, index) => {
                const purchasePrice = source.purchasePrice;
                const renewalPrice = source.renewalPrice;
                const transferPrice = source.transferPrice;

                // Use backend-calculated values if available, otherwise calculate
                const isBestPurchase = source.isBestPurchase !== undefined
                  ? source.isBestPurchase
                  : (purchasePrice !== null && purchasePrice === bestPurchase);
                const isBestRenewal = source.isBestRenewal !== undefined
                  ? source.isBestRenewal
                  : (renewalPrice !== null && renewalPrice === bestRenewal);
                const isBestTransfer = source.isBestTransfer !== undefined
                  ? source.isBestTransfer
                  : (transferPrice !== null && transferPrice === bestTransfer);

                // Use backend-calculated differences if available
                const purchaseDiff = source.purchasePriceDiff !== undefined
                  ? source.purchasePriceDiff
                  : (purchasePrice !== null && bestPurchase !== null && purchasePrice !== bestPurchase ? purchasePrice - bestPurchase : null);
                const renewalDiff = source.renewalPriceDiff !== undefined
                  ? source.renewalPriceDiff
                  : (renewalPrice !== null && bestRenewal !== null && renewalPrice !== bestRenewal ? renewalPrice - bestRenewal : null);
                const transferDiff = source.transferPriceDiff !== undefined
                  ? source.transferPriceDiff
                  : (transferPrice !== null && bestTransfer !== null && transferPrice !== bestTransfer ? transferPrice - bestTransfer : null);

                return (
                  <tr
                    key={index}
                    className={`border-b border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50 ${
                      isBestPurchase || isBestRenewal || isBestTransfer ? 'bg-green-50/30 dark:bg-green-900/10' : ''
                    }`}
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        {/* Provider Logo */}
                        <div className="flex-shrink-0">
                          {source.registrar?.toLowerCase().includes('namecheap') ? (
                            <img
                              src={namecheapLogo}
                              alt="Namecheap"
                              className="h-8 w-auto object-contain"
                            />
                          ) : source.registrar?.toLowerCase().includes('name') ? (
                            <img
                              src={nameComLogo}
                              alt="Name.com"
                              className="h-8 w-auto object-contain"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-white text-sm bg-slate-600">
                              {source.registrar?.charAt(0).toUpperCase()}
                            </div>
                          )}
                        </div>

                      </div>
                    </td>
                    <td className="py-3 px-4 text-right">
                      {purchasePrice !== null ? (
                        <div className="flex flex-col items-end gap-2">
                          <div className="flex items-center gap-2">
                            <span className={`text-lg font-bold ${isBestPurchase ? 'text-green-600 dark:text-green-400' : 'text-slate-900 dark:text-white'}`}>
                              {formatPrice(purchasePrice, source.currency)}
                            </span>
                            {isBestPurchase && (
                              <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 border-green-300 dark:border-green-700 px-2 py-1">
                                <TrendingDown className="w-3 h-3 mr-1" />
                                Best
                              </Badge>
                            )}
                          </div>
                          {purchaseDiff !== null && purchaseDiff > 0 && (
                            <div className="flex items-center gap-1 bg-red-50 dark:bg-red-900/20 px-2 py-1 rounded">
                              <span className="text-sm font-semibold text-red-600 dark:text-red-400">
                                +{formatPrice(purchaseDiff, source.currency)}
                              </span>
                              <span className="text-xs text-red-500 dark:text-red-400">more</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-400 dark:text-slate-500 text-sm">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right">
                      {renewalPrice !== null ? (
                        <div className="flex flex-col items-end gap-2">
                          <div className="flex items-center gap-2">
                            <span className={`text-lg font-bold ${isBestRenewal ? 'text-green-600 dark:text-green-400' : 'text-slate-900 dark:text-white'}`}>
                              {formatPrice(renewalPrice, source.currency)}
                            </span>
                            {isBestRenewal && (
                              <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 border-green-300 dark:border-green-700 px-2 py-1">
                                <TrendingDown className="w-3 h-3 mr-1" />
                                Best
                              </Badge>
                            )}
                          </div>
                          {renewalDiff !== null && renewalDiff > 0 && (
                            <div className="flex items-center gap-1 bg-red-50 dark:bg-red-900/20 px-2 py-1 rounded">
                              <span className="text-sm font-semibold text-red-600 dark:text-red-400">
                                +{formatPrice(renewalDiff, source.currency)}
                              </span>
                              <span className="text-xs text-red-500 dark:text-red-400">more</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-400 dark:text-slate-500 text-sm">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right">
                      {transferPrice !== null ? (
                        <div className="flex flex-col items-end gap-2">
                          <div className="flex items-center gap-2">
                            <span className={`text-lg font-bold ${isBestTransfer ? 'text-green-600 dark:text-green-400' : 'text-slate-900 dark:text-white'}`}>
                              {formatPrice(transferPrice, source.currency)}
                            </span>
                            {isBestTransfer && (
                              <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 border-green-300 dark:border-green-700 px-2 py-1">
                                <TrendingDown className="w-3 h-3 mr-1" />
                                Best
                              </Badge>
                            )}
                          </div>
                          {transferDiff !== null && transferDiff > 0 && (
                            <div className="flex items-center gap-1 bg-red-50 dark:bg-red-900/20 px-2 py-1 rounded">
                              <span className="text-sm font-semibold text-red-600 dark:text-red-400">
                                +{formatPrice(transferDiff, source.currency)}
                              </span>
                              <span className="text-xs text-red-500 dark:text-red-400">more</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-400 dark:text-slate-500 text-sm">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-4">
          <div className="flex items-center gap-1">
            <TrendingDown className="w-3 h-3 text-green-600" />
            <span>Best price</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-red-600">+$X.XX</span>
            <span>Price difference</span>
          </div>
        </div>
      </div>
    );
  }

  const primaryPrice = getPrimaryPrice(primaryPricing);
  const isPremium = isPremiumDomain(primaryPricing);
  const isPurchasable = isPurchasableDomain(primaryPricing);
  const pricingSummary = getPricingSummary(primaryPricing);

  const sizeClasses = {
    sm: 'text-xs px-2 py-1',
    md: 'text-sm px-3 py-1.5',
    lg: 'text-base px-4 py-2'
  };

  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Primary Price Badge with Custom Tooltip */}
      <div className="relative group">
        <Badge
          className={`${getPricingBadgeColor(primaryPricing)} ${sizeClasses[size]} font-bold cursor-help shadow-sm`}
        >
          {primaryPrice ? (
            <>
              <DollarSign className={`${iconSizes[size]} mr-1`} />
              {formatPrice(primaryPrice, primaryPricing?.currency || 'USD')}
            </>
          ) : (
            <>
              <AlertTriangle className={`${iconSizes[size]} mr-1`} />
              No price
            </>
          )}
        </Badge>

        {/* Custom Tooltip */}
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-slate-800 text-white text-xs font-bold rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
          {pricingSummary}
          {/* Tooltip Arrow */}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-slate-800"></div>
        </div>
      </div>

      {/* Premium Badge */}
      {isPremium && (
        <Badge variant="outline" className="bg-yellow-50 text-yellow-900 border-yellow-300 font-bold shadow-sm">
          <Crown className={`${iconSizes[size]} mr-1`} />
          Premium
        </Badge>
      )}

      {/* Not Purchasable Warning */}
      {!isPurchasable && (
        <Badge variant="outline" className="bg-red-50 text-red-800 border-red-300 font-bold shadow-sm">
          <AlertTriangle className={`${iconSizes[size]} mr-1`} />
          Not Available
        </Badge>
      )}

      {/* Detailed Pricing (if requested and single source) */}
      {showDetails && sources.length === 1 && primaryPricing && (
        <div className="text-xs text-gray-600 space-y-1">
          {primaryPricing.purchasePrice !== null && (
            <div>Purchase: {formatPrice(primaryPricing.purchasePrice, primaryPricing.currency)}</div>
          )}
          {primaryPricing.renewalPrice !== null && (
            <div>Renewal: {formatPrice(primaryPricing.renewalPrice, primaryPricing.currency)}</div>
          )}
          {primaryPricing.transferPrice !== null && (
            <div>Transfer: {formatPrice(primaryPricing.transferPrice, primaryPricing.currency)}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default DomainPricingComponent;
